import React, { useState, useCallback } from 'react';
import { Text, Button, Stack, Overlay } from '@moda/om';
import { formatPrice } from '../../lib/formatPrice';
import { useCart } from '../../hooks/useCart';
import { useUser } from '../../hooks/useUser';
import { useCheckout } from '../../hooks/useCheckout';
import { LoadingBalls } from '../LoadingBalls';
import { AffirmPromoMessage } from '../AffirmPromoMessage';
import { UserRewardStatus } from '../../constants';
import { PaymentSummaryRow } from './PaymentSummaryRow';

import './CheckoutPaymentSummary.scss';

enum Mode {
  Resting,
  PlacingOrder
}

export const CheckoutPaymentSummary: React.FC = () => {
  const { canPlaceOrder, handlePlaceOrder } = useCheckout();
  const {
    cart: {
      attributes: {
        paymentSummary: {
          subtotal,
          totalTaxes,
          totalShipping,
          promotionDiscount,
          siteCredit,
          total,
          payToday,
          payLater,
          taxType,
          isVatApplicable,
          isShippingPromotionApplied,
          rewardsAmount
        },
        rewardPoints,
        rewardSummary
      }
    }
  } = useCart();
  const {
    user: { userLoyaltyStatus }
  } = useUser();
  const [mode, setMode] = useState(Mode.Resting);

  const handleSubmit = useCallback(async () => {
    setMode(Mode.PlacingOrder);
    await handlePlaceOrder();
    setMode(Mode.Resting);
  }, [handlePlaceOrder, setMode]);

  return (
    <Stack space={4} className="CheckoutPaymentSummary">
      <Text treatment="h3" family="serif">
        Payment Summary
      </Text>
      <div className="CheckoutPaymentSummary__divider" />
      <PaymentSummaryRow
        label="Order Subtotal"
        value={formatPrice(subtotal.currency, subtotal.price)}
      />
      {!isVatApplicable && (
        <PaymentSummaryRow
          label={taxType}
          tooltip="Taxes depend on the laws of the location to which you are shipping. Duties are determined by each product's origin, material composition, use, and final destination."
          value={formatPrice(totalTaxes.currency, totalTaxes.price)}
        />
      )}
      <PaymentSummaryRow
        label="Shipping"
        value={
          isShippingPromotionApplied
            ? 'Free'
            : formatPrice(totalShipping.currency, totalShipping.price)
        }
      />
      <Text treatment="bold1">
        <PaymentSummaryRow label="Order Total" value={formatPrice(total.currency, total.price)} />
      </Text>
      {rewardSummary?.useLoyaltyRewards && (
        <>
          <div className="CheckoutPaymentSummary__divider" />
          <Text treatment="bold1">Payment Details</Text>
          <PaymentSummaryRow
            label="Pay Today (M'O Rewards)"
            value={`-${formatPrice(rewardsAmount.currency, rewardsAmount.price)}`}
          />
        </>
      )}
      <div className="CheckoutPaymentSummary__divider" />
      {promotionDiscount.price > 0 && (
        <PaymentSummaryRow
          label="Gift Code"
          value={`-${formatPrice(promotionDiscount.currency, promotionDiscount.price)}`}
        />
      )}
      {siteCredit.price > 0 && (
        <PaymentSummaryRow
          label="Site Credit"
          value={`-${formatPrice(siteCredit.currency, siteCredit.price)}`}
        />
      )}
      <Stack space={1}>
        <Text treatment="bold1">
          <PaymentSummaryRow
            label="Pay Today"
            value={formatPrice(payToday.currency, payToday.price)}
          />
        </Text>
        <div className="CheckoutPaymentSummary__affirm-promo">
          {Number(payLater.price) === 0 && (
            <AffirmPromoMessage pageType="cart" price={Number(payToday.price)} />
          )}
        </div>
      </Stack>
      <PaymentSummaryRow
        label="Remaining Balance"
        tooltip="Preorder items are made especially for you and require a 50% deposit at the time of purchase. The remaining balance and sales tax/duties will be charged upon shipping."
        value={formatPrice(payLater.currency, payLater.price)}
      />
      {userLoyaltyStatus === UserRewardStatus.Enrolled && rewardPoints?.toString() !== '0' && (
        <Text treatment="bold1">{`Place your order to earn ${rewardPoints} points!`}</Text>
      )}
      <Button disabled={!canPlaceOrder} onClick={handleSubmit} data-testid="checkoutPlaceYourOrder">
        Place your order
      </Button>

      <Overlay className="CheckoutPaymentSummary__overlay" show={mode === Mode.PlacingOrder}>
        <LoadingBalls />
        <Text treatment="eyebrow">Placing order</Text>
      </Overlay>
    </Stack>
  );
};
